import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { CdkStep, CdkStepper } from '@angular/cdk/stepper';
import { StepperHelper } from '@app/_shared/helpers/StepperHelper';
import { AppState } from '@app/core/store/app.state';
import { GetGeneralInfoApp } from '@app/core/store/app.actions';
import { Observable, combineLatest, filter, map, mapTo, take, timer } from 'rxjs';
import { StepperState } from '../../store/stepper.state';
import { MODULES_OPTIONS } from '@app/_models/images-manager/images-management.model';
import { GetCustomThemes, GetThemes, SetTheme } from '@app/core/store/app.actions';
import {
    InitializeWizard,
    SetIaFinished,
    SetIsLoading,
    SetMarketSectionsIa,
    SetOpenModalStepper,
    SetWizardStatus,
} from '../../store/stepper.actions';
import { CustomTheme, Theme } from '@app/_models/themes/theme.model';
import { MatDialog } from '@angular/material/dialog';
import { CustomizeThemeModalComponent } from './customize-theme-modal/customize-theme-modal.component';
import { RegenerateSiteDialogComponent } from '../../stepper-components/regenerate-site-dialog/regenerate-site-dialog.component';
import { WizardErrorModalComponent } from '../../stepper-components/wizard-error-modal/wizard-error-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { McToastService } from '@app/_services/mc-toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'preview-site-ia',
    templateUrl: './preview-site-ia.component.html',
    styleUrls: ['./preview-site-ia.component.css'],
})
export class PreviewSiteIaComponent implements OnInit, OnDestroy {
    @ViewChild('marketIframe') marketIframe: ElementRef;
    @Input() stepName: string;

    submitted: boolean;
    iframeUrl: string;
    selectedTheme: number;
    loading: boolean = true;
    selectedIsCustom: boolean = true;
    customThemeName = 'Custom';
    customThemePrimary = '#1e1e1e';
    customThemeSecondary = '#f4b063';

    customTheme: CustomTheme = {
        name: 'Custom',
        body: {
            background_color: '#E89623',
        },
        content: {
            font: 'Roboto',
            primary_color: '#DEE1E9',
            secondary_color: '#FAFAFA',
        },
        header: {
            color: '#FAFAFA',
            font_color: '#666666',
        },
        footer: {
            color: '#FAFAFA',
            font_color: '#666666',
        },
        buttons: {
            border_radius: '4px',
        },
    };

    @Select(StepperState.wizardLogoName)
    wizardLogoName$: Observable<any>;

    @Select(AppState.siteAliases)
    siteAliases$: Observable<string>;

    @Select(StepperState.isLoading)
    isLoading$: Observable<boolean>;

    @Select(AppState.customThemeList)
    customThemeList$: Observable<string>;

    @Select(AppState.customSelected)
    customSelected$: Observable<number>;

    @Select(AppState.themes)
    private rawThemes$: Observable<Theme[]>;

    themes$: Observable<Theme[]>;

    module: MODULES_OPTIONS = MODULES_OPTIONS.OUT_MODULE;

    regenerate: boolean = false;

    triesCount: number = 1;
    readonly totalTries: number = 3;

    constructor(
        private store: Store,
        private cdkStepper: CdkStepper,
        private cdkStep: CdkStep,
        public dialog: MatDialog,
        private modalNgb: NgbModal,
        private stepperHelper: StepperHelper,
        private mcToast: McToastService,
        private translate: TranslateService
    ) {
        this.store.dispatch(new GetThemes());
        this.store.dispatch(new SetIaFinished(false));
    }

    ngOnInit(): void {
        const minTime$ = timer(8000).pipe(mapTo(true));
        const iaFinished$ = this.store.select(StepperState.iaFinished).pipe(
            filter((finished) => finished === true),
            take(1)
        );

        combineLatest([minTime$, iaFinished$])
            .pipe(take(1))
            .subscribe(() => {
                this.loading = false;
                this.store.dispatch(new GetCustomThemes());
                this.customSelected$.subscribe((selected) => {
                    if (selected) {
                        this.selectedTheme = selected;
                        this.themes$ = this.rawThemes$.pipe(map((themes) => [...themes].sort((a, b) => a.id - b.id)));
                        this.customThemeList$.subscribe((themes: any) => {
                            const matchingTheme = themes.find((theme) => theme.id === selected);
                            this.customTheme = matchingTheme;
                        });
                    }
                });
            });

        this.iframeUrl = `${this.store.selectSnapshot(AppState.marketSubdomain)}?stats=off&render=true`;
    }

    ngOnDestroy(): void {
        this.stepperHelper.updateStatusStep(this.stepName, this.cdkStep);
    }

    onFormSubmit(): void {
        const themeToSelect = {
            id: this.selectedTheme,
            is_custom: this.selectedIsCustom,
        };
        this.store.dispatch(new SetTheme(themeToSelect)).subscribe({
            complete: () => {
                this.store.dispatch(new SetWizardStatus(true)).subscribe({
                    next: () => {
                        this.store.dispatch([new GetGeneralInfoApp()]).subscribe({
                            complete: () => {
                                this.store.dispatch(new InitializeWizard(false));
                                this.loading = false;
                                this.store.dispatch(new SetOpenModalStepper(true));
                                this.stepperHelper.deleteStatusWizard();
                            },
                        });
                    },
                });
            },
        });
    }

    reloadIframe(theme, custom: boolean): void {
        if (!theme?.id) return;
        this.selectedTheme = theme?.id;
        this.selectedIsCustom = custom;
        this.iframeUrl = '';
        setTimeout(() => {
            const baseUrl = this.store.selectSnapshot(AppState.marketSubdomain);
            this.iframeUrl = custom
                ? `${baseUrl}?theme_preview=custom&stats=off&render=true`
                : `${baseUrl}?theme_preview=${theme.name}&stats=off&render=true`;
        });
    }

    onEditTheme(): void {
        const modal = this.dialog.open(CustomizeThemeModalComponent, {
            panelClass: 'customize-theme-modal',
            data: {
                create: false,
                customTheme: this.customTheme,
            },
        });
        modal.afterClosed().subscribe((customTheme: CustomTheme | any) => {
            if (customTheme) {
                this.customTheme = customTheme;
                this.reloadIframe(this.customTheme, true);
            }
        });
    }

    regenerateSiteDialog(): void {
        const data = this.stepperHelper.getDataStep('create-site-ia');
        const [prefix, prompt] = data.prompt.split('Prompt:');
        const regenerateDialog = this.dialog.open(RegenerateSiteDialogComponent, {
            data: { prompt: prompt.trim(), triesCount: this.triesCount, totalTries: this.totalTries },
        });
        regenerateDialog.afterClosed().subscribe((newPrompt) => {
            if (newPrompt) {
                this.regenerate = true;
                this.loading = true;
                const updatedPrompt = `${prefix}Prompt: ${newPrompt}`;
                data.prompt = updatedPrompt;
                this.stepperHelper.updateDataStep(this.stepName, data);
                const randomTemplate = this.store.selectSnapshot(StepperState.randomTemplate);
                const auxDataTemplates = {
                    pages: randomTemplate.pages.map((p) => ({ id: p.id })),
                    userPrompt: updatedPrompt,
                };
                this.regenerate = true;
                this.store.dispatch(new SetMarketSectionsIa(auxDataTemplates)).subscribe({
                    next: () => {
                        localStorage.setItem('userPrompt', updatedPrompt);
                        this.store.dispatch(new SetIaFinished(true));
                        this.store.dispatch(new SetIsLoading(false));
                        this.regenerate = false;
                        this.loading = false;
                        this.triesCount++;
                        this.mcToast.success(this.translate.instant('app.wizard.stepper_ia.regenerate-success'));
                    },
                    error: (err) => {
                        const modalRef = this.modalNgb.open(WizardErrorModalComponent);
                        let data = { err };
                        modalRef.componentInstance.fromParent = data;
                        this.regenerate = false;
                        this.loading = false;
                    },
                });
            }
        });
    }
}
